import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { userAssetService } from '../services/userAssetService';
import { UserAsset, UserAssetFilters } from '../types';

export const useUserAssets = (filters: UserAssetFilters = {}) => {
  return useQuery({
    queryKey: ['userAssets', filters],
    queryFn: () => userAssetService.getAll(filters),
    staleTime: 5 * 60 * 1000, // 5 minutes
  });
};

export const useMyUserAssets = (filters: UserAssetFilters = {}) => {
  return useQuery({
    queryKey: ['myUserAssets', filters],
    queryFn: () => userAssetService.getMyAssets(filters),
    staleTime: 5 * 60 * 1000, // 5 minutes
  });
};

export const useUserAsset = (id: string) => {
  return useQuery({
    queryKey: ['userAsset', id],
    queryFn: () => userAssetService.getById(id),
    enabled: !!id,
    staleTime: 5 * 60 * 1000, // 5 minutes
  });
};

export const useUserAssetStats = () => {
  return useQuery({
    queryKey: ['userAssetStats'],
    queryFn: () => userAssetService.getStats(),
    staleTime: 10 * 60 * 1000, // 10 minutes
  });
};

export const useSearchUserAssets = () => {
  return useMutation({
    mutationFn: userAssetService.search,
  });
};

export const useDownloadUserAsset = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: userAssetService.download,
    onSuccess: () => {
      // Invalidate user assets queries to refresh download counts
      queryClient.invalidateQueries({ queryKey: ['userAssets'] });
      queryClient.invalidateQueries({ queryKey: ['myUserAssets'] });
    },
  });
};

export const useDownloadPublishedUserAsset = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: userAssetService.downloadPublished,
    onSuccess: () => {
      // Invalidate user assets queries to refresh download counts
      queryClient.invalidateQueries({ queryKey: ['userAssets'] });
      queryClient.invalidateQueries({ queryKey: ['myUserAssets'] });
    },
  });
};

export const useReactivateUserAsset = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, reason }: { id: string; reason?: string }) => userAssetService.reactivate(id, reason),
    onSuccess: (_data, { id }) => {
      queryClient.invalidateQueries({ queryKey: ['userAssets'] });
      queryClient.invalidateQueries({ queryKey: ['userAsset', id] });
      queryClient.invalidateQueries({ queryKey: ['userAssetStats'] });
    },
  });
};

export const useCreateUserAsset = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: userAssetService.create,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['userAssets'] });
      queryClient.invalidateQueries({ queryKey: ['userAssetStats'] });
    },
  });
};

export const useUpdateUserAsset = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: Partial<UserAsset> }) =>
      userAssetService.update(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['userAssets'] });
      queryClient.invalidateQueries({ queryKey: ['myUserAssets'] });
      queryClient.invalidateQueries({ queryKey: ['userAssetStats'] });
    },
  });
};

export const useDeleteUserAsset = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: userAssetService.delete,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['userAssets'] });
      queryClient.invalidateQueries({ queryKey: ['myUserAssets'] });
      queryClient.invalidateQueries({ queryKey: ['userAssetStats'] });
    },
  });
};
